import React, { useState } from "react";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import { 
  Card, 
  CardBody, 
  CardTitle, 
  Row, 
  Col, 
  Badge,
  ListGroup,
  ListGroupItem
} from "reactstrap";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8", "#82ca9d", "#ffc658", "#8dd1e1", "#a4de6c", "#d0ed57", "#999999"];

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip" style={{ 
        backgroundColor: '#fff', 
        padding: '10px', 
        border: '1px solid #ccc',
        borderRadius: '4px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.15)'
      }}>
        <p style={{ fontWeight: 'bold', margin: '0 0 5px 0' }}>{payload[0].name}</p>
        <p style={{ margin: '0' }}>{`${payload[0].value} incidents`}</p>
        <p style={{ margin: '0' }}>{`${(payload[0].payload.percent * 100).toFixed(1)}%`}</p>
      </div>
    );
  }
  return null;
};

const IncidentChart = ({ incidentCounts }) => {
  // Flatten the data structure
  const flattenedData = Object.entries(incidentCounts || {}).flatMap(([regionName, incidents]) =>
    Object.entries(incidents).map(([type, count]) => ({
      name: `${regionName} - ${type}`,
      value: count,
    }))
  );
  
  // Sort by value (count) in descending order
  flattenedData.sort((a, b) => b.value - a.value);
  
  // Calculate the total number of incidents
  const totalIncidents = flattenedData.reduce((sum, item) => sum + item.value, 0);
  
  // Prepare chart data: top 10 + "Other"
  let chartData = [];
  
  if (flattenedData.length > 10) {
    // Get the top 10
    const top10 = flattenedData.slice(0, 10);
    
    // Calculate the sum of the rest
    const otherSum = flattenedData.slice(10).reduce((sum, item) => sum + item.value, 0);
    
    // Add percentage to each data point
    chartData = [
      ...top10.map(item => ({
        ...item,
        percent: item.value / totalIncidents
      })),
      { name: "Other", value: otherSum, percent: otherSum / totalIncidents }
    ];
  } else {
    // If we have 10 or fewer items, use all of them with percentage
    chartData = flattenedData.map(item => ({
      ...item,
      percent: item.value / totalIncidents
    }));
  }

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
  
    return (
      <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // Responsive chart size calculation
  const useResponsiveSize = () => {
    const [size, setSize] = useState({
      chartWidth: window.innerWidth < 768 ? window.innerWidth - 40 : 400,
      chartHeight: window.innerWidth < 768 ? 300 : 400
    });
    
    React.useEffect(() => {
      const handleResize = () => {
        setSize({
          chartWidth: window.innerWidth < 768 ? window.innerWidth - 40 : 400,
          chartHeight: window.innerWidth < 768 ? 300 : 400
        });
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    return size;
  };
  
  const { chartWidth, chartHeight } = useResponsiveSize();

  if (chartData.length === 0) {
    return (
      <Card className="text-center">
        <CardBody>
          <p className="text-muted">No incident data available.</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="mb-4">
      <CardBody>
        <CardTitle tag="h5" className="mb-4 text-center">Incident Distribution</CardTitle>
        
        <Row>
          {/* Chart Column - Full width on mobile, half width on larger screens */}
          <Col xs="12" md="6" style={{justifyItems:'center'}} className="mb-4 text-center justify-content-center">
            <PieChart width={chartWidth} height={chartHeight}>
              <Pie 
                data={chartData} 
                dataKey="value" 
                nameKey="name" 
                cx="50%" 
                cy="50%" 
                outerRadius={Math.min(chartWidth, chartHeight) / 2.5}
                labelLine={false}
                label={renderCustomizedLabel}
              >
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.name === "Other" ? "#999999" : COLORS[index % (COLORS.length - 1)]} 
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </Col>
          
          {/* Legend Column - Full width on mobile, half width on larger screens */}
          <Col xs="12" md="6">
            <div style={{ maxHeight: "400px", overflowY: "auto" }}>
              <ListGroup flush>
                {chartData.map((entry, index) => (
                  <ListGroupItem key={`legend-${index}`} className="d-flex align-items-center py-2 px-3">
                    <Badge 
                      color={entry.name === "Other" ? "secondary" : ""} 
                      style={{ 
                        backgroundColor: entry.name === "Other" ? "#999999" : COLORS[index % (COLORS.length - 1)],
                        width: "12px", 
                        height: "12px",
                        marginRight: "10px"
                      }} 
                      pill
                    />
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="text-truncate" style={{ maxWidth: "70%", textTransform: 'capitalize' }} title={entry.name}>
                          {entry.name}
                        </div>
                        <span className="text-muted small">
                          {entry.value} ({(entry.percent * 100).toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  </ListGroupItem>
                ))}
              </ListGroup>
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

export default IncidentChart;